import { useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import { CALENDAR_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import {
  RssLinkPlatformOptionAlert,
  RssLinkPlatformPreference,
} from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";

import ConfirmationModal from "@rui/ConfirmationModal";
import { Bold } from "@rui/styles";

import { PLATFORM_PREFERENCE_CONFIG } from "./constants";

const EnablePlatformModal = ({
  alert,
  onClose,
  onConnectOrReauthProvider,
  platform,
}: {
  alert: RssLinkPlatformOptionAlert | null;
  onClose: () => void;
  onConnectOrReauthProvider: (
    platform: RssLinkPlatformPreference,
    calendarProviderId: CalendarProviderId
  ) => void;
  platform: RssLinkPlatformPreference | null;
}) => {
  const navigate = useNavigate();

  if (!alert || !platform || platform === "all") return null;

  const { label: platformLabel, calendarProviderId } =
    PLATFORM_PREFERENCE_CONFIG[platform];

  // none of the error states make any sense without a calendar provider, but appease types
  if (!calendarProviderId) return null;

  const calendarProviderDisplayName =
    CALENDAR_PROVIDERS_BY_ID[calendarProviderId].displayName;

  const handleGoToCalendarSettings = () => {
    navigate(ROUTES.INTEGRATIONS_GOOGLE_CALENDAR);
    onClose();
  };

  const commonProps = {
    open: true,
    title: `Enable ${platformLabel}`,
    onClose,
  };

  if (alert === "google_calendar_required" && platform === "meet") {
    return (
      <ConfirmationModal
        {...commonProps}
        onConfirm={handleGoToCalendarSettings}
        confirmProps={{ label: "Go to calendar settings" }}
      >
        <Typography>
          To create {platformLabel} meetings, you must use{" "}
          {calendarProviderDisplayName} as your default calendar.
        </Typography>
      </ConfirmationModal>
    );
  }

  const handleConnectOrReauth = () =>
    onConnectOrReauthProvider(platform, calendarProviderId);

  if (alert === "not_connected") {
    return (
      <ConfirmationModal
        {...commonProps}
        onConfirm={handleConnectOrReauth}
        confirmProps={{ label: `Connect ${calendarProviderDisplayName}` }}
      >
        <Typography>
          {calendarProviderDisplayName} is not connected to Read. Connect a{" "}
          {calendarProviderDisplayName} account to enable {platformLabel}{" "}
          meeting creation.
        </Typography>
      </ConfirmationModal>
    );
  }

  if (alert === "token_is_revoked") {
    return (
      <ConfirmationModal
        {...commonProps}
        onConfirm={handleConnectOrReauth}
        confirmProps={{ label: `Reauthorize ${calendarProviderDisplayName}` }}
      >
        <Typography>
          {calendarProviderDisplayName} requires reauthorization. Reauthorize
          your account to enable {platformLabel} meeting creation.
        </Typography>
      </ConfirmationModal>
    );
  }

  if (alert === "missing_required_scope") {
    return (
      <ConfirmationModal
        {...commonProps}
        onConfirm={handleConnectOrReauth}
        confirmProps={{ label: "Grant edit permission" }}
      >
        <Typography>
          To enable {platformLabel} meeting creation, Read needs to be able to
          edit your <Bold>{calendarProviderDisplayName}</Bold> calendar.
        </Typography>
      </ConfirmationModal>
    );
  }

  return null;
};

export default EnablePlatformModal;
